import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { Markdown } from "@/components/Markdown";
import { Tabs, type TabItem } from "@/components/Tabs";
import { useTranslation } from "react-i18next";

export interface RichTextBasicProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    minHeight?: string;
    autoFocus?: boolean;
    rounded?: boolean;
}

const COMMON_EMOJIS = [
    "😊",
    "👍",
    "🚀",
    "⚠️",
    "💡",
    "📋",
    "✅",
    "❌",
    "🔥",
    "🎯",
    "💬",
    "📌",
];

export const RichTextBasic: React.FC<RichTextBasicProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    className = "",
    minHeight = "180px",
    autoFocus = false,
    rounded = true,
}: RichTextBasicProps) => {
    const { t } = useTranslation("global");
    const ref = useRef<HTMLTextAreaElement>(null);
    const [activeTab, setActiveTab] = useState<"write" | "preview">("write");
    const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);

    useEffect(() => {
        if (autoFocus && ref.current) {
            ref.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        if (ref.current && activeTab === "write") {
            ref.current.style.height = "auto";
            ref.current.style.height = `${Math.max(parseInt(minHeight, 10) || 160, ref.current.scrollHeight)}px`;
        }
    }, [value, activeTab, minHeight]);

    const wrap = (before: string, after = before): void => {
        const element = ref.current;
        if (!element) {
            return;
        }

        const start = element.selectionStart;
        const end = element.selectionEnd;
        const selected = value.slice(start, end) || "texto";
        const next = `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`;
        onChange(next);
        requestAnimationFrame(() => {
            element.focus();
            element.setSelectionRange(
                start + before.length,
                start + before.length + selected.length
            );
        });
    };

    const insert = (text: string): void => {
        const element = ref.current;
        if (!element) {
            return;
        }

        const start = element.selectionStart;
        const end = element.selectionEnd;
        onChange(`${value.slice(0, start)}${text}${value.slice(end)}`);
        requestAnimationFrame(() => {
            element.focus();
            element.setSelectionRange(start + text.length, start + text.length);
        });
    };

    const insertTable = (): void => {
        const tableTemplate =
            "\n| Encabezado 1 | Encabezado 2 | Encabezado 3 |\n| :--- | :--- | :--- |\n| Dato 1 | Dato 2 | Dato 3 |\n| Fila 2 | Fila 2 | Fila 2 |\n";
        insert(tableTemplate);
    };

    const insertLink = (): void => {
        const element = ref.current;
        const start = element?.selectionStart ?? 0;
        const end = element?.selectionEnd ?? 0;
        const selected = value.slice(start, end) || "enlace";
        wrap("[", `](https://ejemplo.com)`);
    };

    return (
        <div
            style={{ minHeight, resize: "vertical" }}
            className={`flex flex-col flex-1 ${rounded ? "rounded-xl" : ""} border border-slate-300 dark:border-white/20 bg-white dark:bg-dark-box overflow-hidden focus-within:ring-2 focus-within:ring-primary/40 resize-y ${className}`}
        >
            {/* Formatting Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-1 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-dark-subtle px-2.5 py-1.5 shrink-0">
                <div className="flex flex-wrap items-center gap-1">
                    {/* Bold */}
                    <button
                        type="button"
                        onClick={() => wrap("**")}
                        className="h-7 w-7 rounded-lg text-xs font-black text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.bold")}
                    >
                        B
                    </button>

                    {/* Italic */}
                    <button
                        type="button"
                        onClick={() => wrap("_")}
                        className="h-7 w-7 rounded-lg text-xs italic font-serif text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.italic")}
                    >
                        I
                    </button>

                    {/* Strikethrough */}
                    <button
                        type="button"
                        onClick={() => wrap("~~")}
                        className="h-7 w-7 rounded-lg text-xs line-through text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.strikethrough")}
                    >
                        S
                    </button>

                    <div className="h-4 w-px bg-slate-200 dark:bg-white/15 mx-0.5" />

                    {/* Heading 2 */}
                    <button
                        type="button"
                        onClick={() => insert("\n## ")}
                        className="h-7 px-1.5 rounded-lg text-xs font-bold text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.h2")}
                    >
                        H2
                    </button>

                    {/* Heading 3 */}
                    <button
                        type="button"
                        onClick={() => insert("\n### ")}
                        className="h-7 px-1.5 rounded-lg text-xs font-semibold text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.h3")}
                    >
                        H3
                    </button>

                    <div className="h-4 w-px bg-slate-200 dark:bg-white/15 mx-0.5" />

                    {/* Bullet list */}
                    <button
                        type="button"
                        onClick={() => insert("\n- ")}
                        className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.bullet_list")}
                    >
                        <Icon value="list" size={14} />
                    </button>

                    {/* Numbered list */}
                    <button
                        type="button"
                        onClick={() => insert("\n1. ")}
                        className="h-7 px-1.5 rounded-lg text-xs font-semibold text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.numbered_list")}
                    >
                        1.
                    </button>

                    {/* Quote */}
                    <button
                        type="button"
                        onClick={() => insert("\n> ")}
                        className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.quote")}
                    >
                        <Icon value="quote" size={13} />
                    </button>

                    {/* Code */}
                    <button
                        type="button"
                        onClick={() => wrap("`")}
                        className="h-7 px-1.5 rounded-lg text-xs font-mono text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.code")}
                    >
                        &lt;/&gt;
                    </button>

                    {/* Table */}
                    <button
                        type="button"
                        onClick={insertTable}
                        className="h-7 px-2 rounded-lg text-xs font-semibold text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer inline-flex items-center gap-1"
                        title={t("rich_text.insert_table")}
                    >
                        <Icon value="table" size={13} />
                        <span>{t("rich_text.table")}</span>
                    </button>

                    {/* Link */}
                    <button
                        type="button"
                        onClick={insertLink}
                        className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer"
                        title={t("rich_text.link")}
                    >
                        <Icon value="link" size={13} />
                    </button>

                    {/* Emojis dropdown */}
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="h-7 px-1.5 rounded-lg text-xs text-slate-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-white/10 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-colors cursor-pointer inline-flex items-center gap-1"
                            title={t("rich_text.emojis")}
                        >
                            <span>😊</span>
                            <Icon value="chevronDown" size={10} />
                        </button>

                        {showEmojiPicker && (
                            <div className="absolute left-0 top-full mt-1 p-2 bg-white dark:bg-dark-box rounded-xl shadow-xl border border-slate-200 dark:border-white/20 grid grid-cols-6 gap-1 z-30 min-w-[180px]">
                                {COMMON_EMOJIS.map((emoji) => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => {
                                            insert(` ${emoji} `);
                                            setShowEmojiPicker(false);
                                        }}
                                        className="h-7 w-7 flex items-center justify-center text-base hover:bg-slate-100 dark:hover:bg-white/10 rounded cursor-pointer transition-colors"
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Tab: Write vs Preview */}
                <Tabs
                    variant="pills"
                    syncUrl={false}
                    activeTab={activeTab}
                    onChange={(id) => setActiveTab(id as "write" | "preview")}
                    items={[
                        {
                            id: "write",
                            label: t("rich_text.edit"),
                            icon: <Icon value="code" size={14} />,
                        },
                        {
                            id: "preview",
                            label: t("rich_text.preview"),
                            icon: <Icon value="eye" size={14} />,
                        },
                    ]}
                />
            </div>

            {/* Content Area: Write or Preview */}
            <div className="flex-1 flex flex-col min-h-[160px] overflow-y-auto">
                {activeTab === "write" ? (
                    <textarea
                        ref={ref}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                        className="block flex-1 w-full resize-y border-0 bg-transparent px-3.5 py-3 text-xs text-slate-800 dark:text-zinc-200 outline-none ring-0 leading-relaxed font-sans placeholder:text-slate-400 dark:placeholder:text-zinc-500"
                        style={{ minHeight }}
                    />
                ) : (
                    <div className="p-4 flex-1 bg-white dark:bg-dark-box text-xs leading-relaxed font-sans overflow-y-auto">
                        {value.trim() ? (
                            <Markdown>{value}</Markdown>
                        ) : (
                            <span className="text-slate-400 italic">
                                No hay contenido redactado todavía.
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RichTextBasic;
